/**
 * 9:16 Aramgah share-card. Canvas-rendered Insta-grade tier flex.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, CHRONOTYPE_DISPLAY, formatHours } from '../types.ts';
import type { SleepSummary } from '../types.ts';

export class AramgahShareCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #current: SleepSummary | null = null;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      this.#current = s.summary;
      this.#render();
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: grid; gap: 10px; }
button {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 16px;
  background: var(--moon);
  color: var(--paper);
  border-radius: 8px;
  font-weight: 700;
}
button:active { filter: brightness(0.92); }
button.ghost {
  background: transparent;
  color: var(--ink-dim);
  border: 1px solid var(--rule);
}
</style>
<button id="share">Share raat ki kahaani</button>
<button class="ghost" id="download">Download 9:16 PNG</button>
`;
    this.#shadow.getElementById('share')?.addEventListener('click', () => this.#share());
    this.#shadow.getElementById('download')?.addEventListener('click', () => this.#download());
  }

  async #renderCanvas(): Promise<Blob | null> {
    if (!this.#current) return null;
    const W = 1080, H = 1920;
    const canvas = new OffscreenCanvas(W, H);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const s = this.#current;
    const tier = s.tier;
    const meta = TIER_DISPLAY[tier];
    const chrono = CHRONOTYPE_DISPLAY[s.chronotype];

    ctx.fillStyle = '#0c0a1f';
    ctx.fillRect(0, 0, W, H);
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, 'rgba(192, 192, 208, 0.16)');
    grad.addColorStop(1, 'rgba(12, 10, 31, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#dce3f0';
    ctx.font = '700 60px "Cormorant Garamond", serif';
    ctx.textAlign = 'left';
    ctx.fillText('आरामगाह', 80, 200);

    ctx.fillStyle = 'rgba(220, 227, 240, 0.5)';
    ctx.font = '500 22px "Space Mono", monospace';
    ctx.fillText('ARAMGAH · 30-NIGHT KAHAANI', 80, 240);

    ctx.fillStyle = '#c0c0d0';
    ctx.font = '500 32px "Space Mono", monospace';
    ctx.fillText('RUTBA', 80, 420);
    ctx.fillStyle = '#dce3f0';
    ctx.font = 'italic 700 110px "Cormorant Garamond", serif';
    ctx.fillText(meta.name, 80, 530);
    ctx.fillStyle = 'rgba(220, 227, 240, 0.7)';
    ctx.font = 'italic 500 36px "Cormorant Garamond", serif';
    ctx.fillText(`"${meta.tagline}"`, 80, 590);

    ctx.fillStyle = 'rgba(220, 227, 240, 0.6)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('AVG / NIGHT', 80, 780);
    ctx.fillStyle = '#dce3f0';
    ctx.font = '700 160px "Cormorant Garamond", serif';
    ctx.fillText(formatHours(s.avg_hours), 80, 940);

    ctx.fillStyle = 'rgba(220, 227, 240, 0.6)';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('QUALITY', 80, 1020);
    ctx.fillStyle = '#dce3f0';
    ctx.font = '700 64px "Cormorant Garamond", serif';
    ctx.fillText(Math.round(s.avg_quality) + ' / 100', 80, 1090);

    ctx.fillStyle = '#c0c0d0';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('CHRONOTYPE', 80, 1260);
    ctx.fillStyle = '#dce3f0';
    ctx.font = 'italic 500 56px "Cormorant Garamond", serif';
    ctx.fillText(chrono.name, 80, 1340);
    ctx.fillStyle = 'rgba(220, 227, 240, 0.7)';
    ctx.font = 'italic 500 30px "Cormorant Garamond", serif';
    ctx.fillText(`"${chrono.tagline}"`, 80, 1390);

    ctx.fillStyle = '#c0c0d0';
    ctx.font = '500 24px "Space Mono", monospace';
    ctx.fillText('BEDTIME', 80, 1520);
    ctx.fillText('WAKE', W / 2 + 40, 1520);
    ctx.fillStyle = '#dce3f0';
    ctx.font = '700 64px "Cormorant Garamond", serif';
    ctx.fillText(s.insights.avg_bedtime, 80, 1600);
    ctx.fillText(s.insights.avg_wake_time, W / 2 + 40, 1600);

    ctx.fillStyle = 'rgba(220, 227, 240, 0.4)';
    ctx.font = '500 20px "Space Mono", monospace';
    ctx.fillText('aramgah.calcnook.com', 80, H - 100);
    ctx.fillStyle = '#c0c0d0';
    ctx.fillText('moon', W - 130, H - 100);

    return await canvas.convertToBlob({ type: 'image/png' });
  }

  async #share(): Promise<void> {
    const blob = await this.#renderCanvas();
    if (!blob) return;
    const file = new File([blob], 'aramgah-raat.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: 'My Aramgah', text: 'My 30-night kahaani' }); }
      catch { this.#download(blob); }
    } else { this.#download(blob); }
  }

  async #download(b?: Blob): Promise<void> {
    const blob = b ?? await this.#renderCanvas();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aramgah-raat.png';
    a.click();
    URL.revokeObjectURL(url);
  }
}

customElements.define('aramgah-share-card', AramgahShareCard);
